import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "flex flex-col justify-between px-[34px] min-h-screen pb-[100px]" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { class: "flex grow items-center justify-center w-full" }
const _hoisted_4 = { class: "font-sourcesanspro-semibold text-l2" }
const _hoisted_5 = { class: "flex flex-col items-center justify-center mb-8 mt-8" }
const _hoisted_6 = { class: "font-sourcesanspro-semibold text-lg2" }
const _hoisted_7 = { class: "font-sourcesanspro-regular text-base2 text-white/50" }
const _hoisted_8 = { class: "text-white/30 text-sm mt-1" }
const _hoisted_9 = { class: "fixed bottom-4 left-0 right-0 z-10 bg-transparent" }
const _hoisted_10 = { class: "max-w-[600px] mx-auto px-4 pt-4 pb-[env(safe-area-inset-bottom)]" }

import { computed, watch, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { IonButton, IonContent } from '@ionic/vue';
import { useI18n } from 'vue-i18n';

import TopBar from '@/shared/components/TopBar.vue';
import TopUpItemsSummary from '@/shared/components/ui/TopUpItemsSummary.vue';
import hexRgb from 'hex-rgb';
import rgbHex from 'rgb-hex';
import { TopUp } from '@/enums/values.enum';

import BackButton from '@/shared/components/BackButton.vue';
import { useTransactionStore } from '@/stores/transaction/transactionStore';
import { useSharedStore } from '@/stores/shared/sharedStore';
import useTopUp from '@/modules/top-up/composables/useTopUp';
import useTenant from '@/shared/composables/useTenant';
import useWallet from '@/modules/wallet/composables/useWallet';
import { TransactionSubtype } from '@/enums/wallet.enum';
import useStorageService from '@/shared/composables/useStorageService';
import { WalletRfidCreationCost } from '@/shared/interfaces/wallet';
import type { PreferredPaymentMode } from '@/modules/top-up/composables/useTopUp';


export default /*@__PURE__*/_defineComponent({
  __name: 'TopUpOverviewPage',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const walletId = route.params.wallet_id?.toString();
const encryptedRfidTag = route.query.id?.toString();
const loginToken = route.query.loginToken?.toString() || '';
const topUpType = route.query.topUpType?.toString();
const packageId = route.query.packageId ? +route.query.packageId : 0;

const sharedStore = useSharedStore();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();
const { getMinimumTopUpAmount, getRfidCreationCost, selectedWallet } = useWallet();
const effectiveWalletId = computed(() => selectedWallet.value?.id?.toString() || walletId);
const { minimumTopUp } = getMinimumTopUpAmount(effectiveWalletId);
const { get } = useStorageService();
const rfidCreationCost = ref<WalletRfidCreationCost>();

const primary_color = window.primary_color;
const primaryRgb = hexRgb(window.primary_color, { format: 'array' });
const primaryShade = '#' + rgbHex(`rgb(${primaryRgb[0]}, ${primaryRgb[1]}, ${primaryRgb[2]})`);

const transactionStore = useTransactionStore();
const transaction = computed(() => transactionStore.getItemsAndTotal());

const { startTopUp, getApplicablePaymentCountry, formValue, selectTopUp } = useTopUp(tenantConfig, minimumTopUp);
const activePreferredPaymentMode = computed<PreferredPaymentMode>(() => {
	if (!sharedStore.shouldUsePreferredPaymentMode) return null;
	if (tenantConfig.value?.viva_wallet_click_to_pay_preferred_mode) return 'click_to_pay';
	if (tenantConfig.value?.viva_wallet_payconiq_preferred_mode) return 'payconiq';
	return null;
});

const visibleItems = computed(() => transactionStore.items);

const topUpLabel = computed(() => {
	if (transaction.value.subtype === TransactionSubtype.PACKAGE) return t('top_up.packages');
	if (transaction.value.subtype === TransactionSubtype.FREE_AMOUNT) return t('top_up.free_amount');
	return '';
});

const walletReceivesCurrency = computed(() => {
	if (selectedWallet.value && selectedWallet.value.currency.name !== 'EUR') {
		return selectedWallet.value.currency.prefix;
	}
	return '€';
});

const isPackageTopUp = computed(() => transaction.value.subtype === TransactionSubtype.PACKAGE);

const onContinue = async () => {
	formValue.value = transactionStore.totalPriceWithoutFee;

	const paymentMethodCountry = getApplicablePaymentCountry();
	if (topUpType === TopUp.TopUpTypeFreeAmount) {
		if (!selectedWallet.value) {
			selectedWallet.value = await get('selectedWallet');
		}
		startTopUp(paymentMethodCountry, activePreferredPaymentMode.value, encryptedRfidTag, effectiveWalletId.value, selectedWallet.value?.currency.id, loginToken);
	} else if (topUpType === TopUp.TopUpTypePackages) {
		selectTopUp(packageId, paymentMethodCountry, activePreferredPaymentMode.value, encryptedRfidTag, loginToken);
	}
};


onMounted(async () => {
	if (!selectedWallet.value) {
		selectedWallet.value = await get('selectedWallet');
	}
	if (effectiveWalletId.value) {
		rfidCreationCost.value = await getRfidCreationCost(effectiveWalletId.value);
		if (rfidCreationCost.value && rfidCreationCost.value.wallet_rfid_creation_cost > 0) {
			transactionStore.setRfidCost(+rfidCreationCost.value.wallet_rfid_creation_cost, t('transaction.card_creation_cost'));
			transactionStore.totalPriceNumber = transactionStore.totalPriceNumber + rfidCreationCost.value.wallet_rfid_creation_cost;
		}
	}
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonContent), {
    style: _normalizeStyle(`background: linear-gradient(${_unref(primary_color)}, ${primaryShade});`)
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (_unref(transactionStore).items.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createVNode(TopBar, null, {
                left: _withCtx(() => [
                  _createVNode(BackButton)
                ]),
                center: _withCtx(() => [
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)('topup.title')), 1)
                  ])
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("h1", _hoisted_6, _toDisplayString(_unref(t)('checkout.title')), 1),
                _createElementVNode("span", _hoisted_7, _toDisplayString(visibleItems.value.length) + " " + _toDisplayString(_unref(t)('checkout.items')), 1),
                _createElementVNode("span", _hoisted_8, "(" + _toDisplayString(topUpLabel.value) + ")", 1)
              ]),
              _createVNode(TopUpItemsSummary, {
                items: visibleItems.value,
                "total-price": transaction.value.totalPriceNumber,
                "wallet-receives": isPackageTopUp.value ? undefined : transaction.value.totalPriceWithoutFee,
                "wallet-receives-currency": walletReceivesCurrency.value,
                title: _unref(t)('checkout.order_details'),
                "total-title": _unref(t)('checkout.items_total')
              }, null, 8, ["items", "total-price", "wallet-receives", "wallet-receives-currency", "title", "total-title"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            (visibleItems.value.length > 0)
              ? (_openBlock(), _createBlock(_unref(IonButton), {
                  key: 0,
                  disabled: !visibleItems.value.length,
                  class: "w-full hover:rounded-full hover:bg-white",
                  expand: "full",
                  size: "large",
                  shape: "round",
                  onClick: onContinue
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", null, _toDisplayString(_unref(t)('general.continue')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]))
              : _createCommentVNode("", true)
          ])
        ])
      ])
    ]),
    _: 1
  }, 8, ["style"]))
}
}

})